import React, { Component, ReactNode } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorMessage: string;
}

// Stale-deploy recovery: when webpack lazy-imports request a chunk hash
// the server no longer has (typical after a frontend redeploy), the browser
// gets HTML back from nginx's SPA fallback and React throws ChunkLoadError.
// Force a hard reload once so the user picks up the fresh index.html, which
// references the new chunk hashes. Guarded by sessionStorage so a genuinely
// missing chunk doesn't trap the user in a reload loop.
const CHUNK_RELOAD_FLAG = 'midas:chunk-reloaded-once';

const isChunkLoadError = (err: unknown): boolean => {
  if (!err) return false;
  const e = err as { name?: string; message?: string; code?: string };
  if (e.name === 'ChunkLoadError') return true;
  if (e.code === 'CSS_CHUNK_LOAD_FAILED') return true;
  const msg = (e.message || '').toLowerCase();
  return (
    msg.includes('loading chunk') ||
    msg.includes('loading css chunk') ||
    msg.includes("unexpected token '<'")
  );
};

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, errorMessage: '' };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    if (isChunkLoadError(error)) {
      return {
        hasError: true,
        errorMessage:
          'A newer version of the app is available. Reloading…',
      };
    }
    return {
      hasError: true,
      errorMessage: 'Something went wrong. Please try again later.',
    };
  }

  componentDidCatch(error: Error) {
    console.error('Caught by Error Boundary:', error);

    // Auto-recover from stale-chunk errors with one hard reload.
    if (isChunkLoadError(error)) {
      try {
        const alreadyReloaded =
          sessionStorage.getItem(CHUNK_RELOAD_FLAG) === '1';
        if (!alreadyReloaded) {
          sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
          // Cache-bust the reload so any cached index.html is bypassed.
          window.location.reload();
        }
      } catch {
        // sessionStorage can throw in some private-browsing modes — fall back
        // to a normal reload anyway.
        window.location.reload();
      }
    } else {
      // Non-chunk error: clear the reload guard so a later legitimate
      // ChunkLoadError can still trigger a reload.
      try {
        sessionStorage.removeItem(CHUNK_RELOAD_FLAG);
      } catch {
        /* ignore */
      }
    }
  }

  resetError = () => {
    this.setState({ hasError: false, errorMessage: '' });
  };

  render() {
    if (this.state.hasError) {
      return <ErrorPopup message={this.state.errorMessage} onClose={this.resetError} />;
    }
    return this.props.children;
  }
}

interface ErrorPopupProps {
  message: string;
  onClose: () => void;
}

const ErrorPopup: React.FC<ErrorPopupProps> = ({ message, onClose }) => {
  const handleOkay = () => {
    onClose();
    window.location.href = '/';
  };

  return (
    <Dialog open={true} onClose={handleOkay} maxWidth="sm" fullWidth>
      <DialogTitle style={{ textAlign: 'center', fontWeight: 'bold', color: '#d32f2f' }}>
        No Data Available
      </DialogTitle>
      <DialogContent style={{ textAlign: 'center', fontSize: '18px', padding: '20px' }}>
        {message}
      </DialogContent>
      <DialogActions style={{ justifyContent: 'center' }}>
        <Button variant="contained" color="primary" onClick={handleOkay}>
          Okay
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ErrorBoundary;
